import React from "react"
import PropTypes from "prop-types"
import styles from "./index.module.scss"
import SocialComponent from "@/components/SocialComponent"
import { Box } from "@kohler-dsm-library/kohler-core"
import { renderRichText, applyLocaleToUrl } from "@/utils/formatters"
import { useRouter } from "next/router"

const hasItalicMark = doc => {
  if (!doc?.content) return false
  const walk = nodes =>
    nodes.some(
      node =>
        node.marks?.some(m => m.type === "italic") ||
        (node.content && walk(node.content))
    )
  return walk(doc.content)
}

const ArticleImageIcon = ({ articleImageIconProps = {} }) => {
  const { locale } = useRouter()
  const {
    mod = "white",
    image = {},
    heading = "",
    headingFontSize,
    description = "",
    richDescription = "",
    textPosition = "",
    showVideo = false,
    videoUrl = "",
    showPrintAndShareIcon = true,
    cta = {},
    showContentAboveImage = false,
    showContentBelowImage = true,
    showLine = true,
    ctaPosition,
    spacer = false,
    topSpacer = false,
  } = articleImageIconProps || {}
  const description2 = renderRichText(richDescription, locale) || ""
  const containsItalic = hasItalicMark(richDescription)
  const {
    label = "",
    url = "",
    mediaUrl = "",
    ctaType = "",
  } = cta?.fields || {}
  const {
    imageUrl = "",
    altText = "",
    asset = {},
    externalUrl = "",
  } = image?.fields || {}
  const { url: assetUrl = "" } = asset?.fields?.file || {}
  const isCtaAbove = ctaPosition === "above" || !ctaPosition
  const isCtaBelow = ctaPosition === "below"
  const linkBlock = label && ctaType !== "Button" && (
    <div
      className={`${
        textPosition
          ? styles[`h1-container-${textPosition}`]
          : styles["h1-container"]
      } ${isCtaBelow ? "mt-5" : ""}`}
    >
      <div
        className={
          textPosition
            ? styles[`cta-container-${textPosition}`]
            : styles["cta-container"]
        }
      >
        <a
          className={
            isCtaBelow
              ? styles["koh-article-pages-link-below"]
              : styles["koh-article-pages-link"]
          }
          href={applyLocaleToUrl(url || mediaUrl, locale)}
          style={{
            color: "#337ab7",
            fontFamily: "HelveticaBold, Helvetica, Arial, sans-serif",
          }}
        >
          {label}
        </a>
      </div>
    </div>
  )
  //  BUTTON CTA
  const buttonBlock = label && ctaType === "Button" && (
    <div
      className={
        textPosition
          ? styles[`h1-container-${textPosition}`]
          : styles["h1-container"]
      }
    >
      <div className={styles["cta-container-below"]}>
        <a
          href={applyLocaleToUrl(url || mediaUrl, locale)}
          className={`${styles.articlePagesTextLink} ${styles.whiteBgButton}`}
        >
          {label}
        </a>
      </div>
    </div>
  )
  // FINAL CTA (either link or button)
  const finalCta = linkBlock || buttonBlock
  return (
    <Box
      className={styles["c-koh-article-pages"]}
      style={{ backgroundColor: mod }}
      mod={mod}
    >
      <div
        className={
          showContentBelowImage
            ? `belowContainer ${showLine}`
            : `koh-article-pages-container ${showLine}`
        }
        style={{ paddingTop: topSpacer ? "30px" : "0px" }}
      >
        {/* CONTENT ABOVE IMAGE */}
        {showContentAboveImage && (
          <div
            className={
              textPosition
                ? styles[`h1-container-${textPosition}`]
                : styles["h1-container"]
            }
          >
            {heading && (
              <h1
                className={`${styles["koh-article-pages-title"]} ${styles["h1-text"]}`}
              >
                {heading}
              </h1>
            )}
            {description2 && (
              <div
                className={`${styles["koh-article-pages-description"]}`}
                style={{
                  textAlign: textPosition === "center" ? "center" : "left",
                }}
              >
                {description2}
              </div>
            )}
            {description && (
              <div className={styles["koh-article-pages-description"]}>
                {description}
              </div>
            )}
            {/* CTA ABOVE */}
            {isCtaAbove && finalCta}
          </div>
        )}
        {/* IMAGE / VIDEO */}
        {(imageUrl || showVideo || assetUrl) && (
          <a
            href={externalUrl || "javascript:void(0)"}
            target={/^https?:\/\//i.test(externalUrl) ? "_blank" : "_self"}
            rel={
              /^https?:\/\//i.test(externalUrl)
                ? "noopener noreferrer"
                : undefined
            }
            className={
              externalUrl ? styles["link-wrapper"] : styles["no-link-wrapper"]
            }
          >
            <div className={styles["koh-article-pages-banner"]}>
              {showVideo && (
                <div className={styles["video-wrapper"]}>
                  <iframe
                    className={styles["iframe-video"]}
                    title="Article video content"
                    src={videoUrl}
                    allowFullScreen
                  />
                </div>
              )}
              {(imageUrl || assetUrl) && !showVideo && (
                <img src={imageUrl || assetUrl} alt={altText} />
              )}
            </div>
          </a>
        )}
        {/* CTA BELOW */}
        {isCtaBelow && finalCta}
        {/* CONTENT BELOW IMAGE */}
        {showContentBelowImage && (
          <div
            className={
              textPosition
                ? styles[`h1-container-${textPosition}`]
                : styles["h1-container"]
            }
          >
            {heading && (
              <h1
                className={`${styles["koh-article-pages-title"]} ${styles["h1-text"]} font-[HelveticaLight, Helvetica, sans-serif]`}
                style={{
                  fontSize: headingFontSize ? `${headingFontSize}px` : "",
                }}
              >
                {heading}
              </h1>
            )}
            {description2 && (
              <p
                className={`${styles["koh-article-pages-description"]} ${containsItalic ? styles["has-italic"] : ""}`}
                style={{
                  textAlign: textPosition === "center" ? "center" : "left",
                  paddingTop: spacer ? "10px" : "30px",
                  paddingBottom: spacer ? "0px" : "30px",
                }}
              >
                {description2}
              </p>
            )}
            {description && (
              <p className={styles["koh-article-pages-description"]}>
                {description}
              </p>
            )}
            {showPrintAndShareIcon && <SocialComponent />}
          </div>
        )}
      </div>
    </Box>
  )
}
ArticleImageIcon.propTypes = {
  articleImageIconProps: PropTypes.shape({
    mod: PropTypes.string,
    image: PropTypes.object,
    heading: PropTypes.string,
    description: PropTypes.string,
    textPosition: PropTypes.string,
    showVideo: PropTypes.bool,
    videoUrl: PropTypes.string,
    showPrintAndShareIcon: PropTypes.bool,
    cta: PropTypes.object,
    showContentAboveImage: PropTypes.bool,
    showContentBelowImage: PropTypes.bool,
    ctaPosition: PropTypes.string,
  }),
}
export default ArticleImageIcon
